import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { WhatsappLogo, EnvelopeSimple, Phone, MagnifyingGlass } from "@phosphor-icons/react";

const STAGES = [
  { key: "new",        label: "New",        color: "bg-blue-50 border-blue-200 text-blue-800" },
  { key: "contacted",  label: "Contacted",  color: "bg-indigo-50 border-indigo-200 text-indigo-800" },
  { key: "qualified",  label: "Qualified",  color: "bg-violet-50 border-violet-200 text-violet-800" },
  { key: "viewing",    label: "Viewing",    color: "bg-amber-50 border-amber-200 text-amber-800" },
  { key: "offer",      label: "Offer",      color: "bg-emerald-50 border-emerald-200 text-emerald-800" },
  { key: "closed",     label: "Closed",     color: "bg-neutral-100 border-neutral-300 text-neutral-700" },
];

// Priority ladder — how "hot" a lead is by age since creation.
function priorityBadge(createdAtISO) {
  const hours = (Date.now() - new Date(createdAtISO).getTime()) / 36e5;
  if (hours < 2) return { label: "🔥 Fresh",   cls: "text-red-700 bg-red-50 border-red-200" };
  if (hours < 24) return { label: "Today",     cls: "text-amber-700 bg-amber-50 border-amber-200" };
  if (hours < 72) return { label: "3-day",     cls: "text-neutral-700 bg-neutral-50 border-neutral-200" };
  return                   { label: "Stale",   cls: "text-neutral-500 bg-neutral-50 border-neutral-200" };
}

function whatsappLink(phone, name, listing) {
  const digits = (phone || "").replace(/\D/g, "");
  const text = encodeURIComponent(
    `Hi ${name || "there"}, this is Point Penta Real Estate` +
    (listing ? ` — reaching out about your enquiry on ${listing}.` : "."),
  );
  return `https://wa.me/${digits}?text=${text}`;
}

export default function Leads() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads");
      setItems(data.items);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function moveTo(id, stage) {
    await api.put(`/admin/leads/${id}`, { stage });
    setItems((prev) => prev.map((l) => (l.id === id ? { ...l, stage } : l)));
    if (selected?.id === id) setSelected({ ...selected, stage });
    toast.success(`Moved to ${stage}`);
  }

  async function saveNotes() {
    if (!selected) return;
    await api.put(`/admin/leads/${selected.id}`, {
      notes: selected.notes || "",
      next_action_at: selected.next_action_at || null,
    });
    toast.success("Lead updated");
    load();
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((l) => {
      if (stageFilter !== "all" && (l.stage || "new") !== stageFilter) return false;
      if (!q) return true;
      return [l.name, l.email, l.phone, l.listing_title, l.message]
        .some((f) => (f || "").toLowerCase().includes(q));
    });
  }, [items, query, stageFilter]);

  const counts = useMemo(() => {
    const map = { all: items.length };
    STAGES.forEach((s) => { map[s.key] = items.filter((l) => (l.stage || "new") === s.key).length; });
    return map;
  }, [items]);

  return (
    <div className="p-10" data-testid="admin-leads">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.24em] uppercase text-gray-500 mb-1">CRM · {items.length} leads</div>
          <h1 className="font-serif text-4xl">Lead pipeline</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, phone, listing…"
              className="pl-9 pr-3 py-2 text-sm border border-neutral-300 w-72 focus:outline-none focus:border-[#001588]"
              data-testid="lead-search"
            />
          </div>
        </div>
      </div>

      {/* Stage-count filter strip */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setStageFilter("all")}
          className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition ${
            stageFilter === "all" ? "bg-[#001588] text-white border-[#001588]" : "bg-white text-gray-700 border-neutral-300 hover:border-[#001588]"
          }`}
          data-testid="lead-filter-all"
        >
          All ({counts.all})
        </button>
        {STAGES.map((s) => (
          <button
            key={s.key}
            onClick={() => setStageFilter(s.key)}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition ${
              stageFilter === s.key ? "bg-[#001588] text-white border-[#001588]" : `${s.color} hover:border-[#001588]`
            }`}
            data-testid={`lead-filter-${s.key}`}
          >
            {s.label} ({counts[s.key] || 0})
          </button>
        ))}
      </div>

      {loading && <div className="text-sm text-gray-500">Loading leads…</div>}

      {/* Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4 overflow-x-auto pb-4">
        {STAGES.map((col) => {
          const list = filtered.filter((i) => (i.stage || "new") === col.key);
          return (
            <div key={col.key} className="bg-gray-50 border border-gray-200 min-h-[70vh]" data-testid={`kanban-col-${col.key}`}>
              <div className={`px-4 py-3 border-b border-gray-200 flex items-center justify-between ${col.color}`}>
                <span className="text-xs uppercase tracking-wider font-medium">{col.label}</span>
                <span className="text-xs">{list.length}</span>
              </div>
              <div className="p-2 space-y-2">
                {list.map((l) => {
                  const p = priorityBadge(l.created_at);
                  return (
                    <button
                      key={l.id} onClick={() => setSelected(l)}
                      className="w-full text-left bg-white border border-gray-200 p-3 hover:border-[#001588] transition-colors group"
                      data-testid={`lead-card-${l.id}`}
                    >
                      <div className="flex justify-between items-start gap-2 mb-1">
                        <div className="text-sm font-medium truncate">{l.name}</div>
                        <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 border shrink-0 ${p.cls}`}>{p.label}</span>
                      </div>
                      <div className="text-xs text-gray-500 truncate">{l.email || l.phone || "—"}</div>
                      {l.listing_title && <div className="text-xs text-[#001588] mt-1 truncate">{l.listing_title}</div>}
                      {l.source && <div className="text-[10px] text-gray-400 mt-1 uppercase tracking-wider">{l.source}</div>}
                    </button>
                  );
                })}
                {list.length === 0 && <div className="text-xs text-gray-400 p-3 text-center">Drop-zone empty</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail drawer with quick actions */}
      {selected && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-end" onClick={() => setSelected(null)} data-testid="lead-drawer">
          <div className="w-full max-w-md h-full bg-white p-8 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="text-xs tracking-[0.24em] uppercase text-gray-500 mb-1">Lead</div>
            <h2 className="font-serif text-3xl mb-1">{selected.name}</h2>
            <div className="text-xs text-neutral-500 uppercase tracking-wider mb-6">Received {new Date(selected.created_at).toLocaleString()}</div>

            {/* Quick-action buttons — the single biggest agent time-saver */}
            <div className="grid grid-cols-3 gap-2 mb-6">
              {selected.phone && (
                <a href={whatsappLink(selected.phone, selected.name, selected.listing_title)} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-1 p-3 border border-neutral-200 hover:border-green-500 hover:bg-green-50 transition" data-testid="lead-whatsapp">
                  <WhatsappLogo size={20} className="text-green-600" />
                  <span className="text-[10px] uppercase tracking-wider">WhatsApp</span>
                </a>
              )}
              {selected.phone && (
                <a href={`tel:${selected.phone}`} className="flex flex-col items-center gap-1 p-3 border border-neutral-200 hover:border-[#001588] hover:bg-blue-50 transition" data-testid="lead-call">
                  <Phone size={20} className="text-[#001588]" />
                  <span className="text-[10px] uppercase tracking-wider">Call</span>
                </a>
              )}
              {selected.email && (
                <a href={`mailto:${selected.email}?subject=${encodeURIComponent(`Point Penta — ${selected.listing_title || "your enquiry"}`)}`} className="flex flex-col items-center gap-1 p-3 border border-neutral-200 hover:border-[#001588] hover:bg-blue-50 transition" data-testid="lead-email">
                  <EnvelopeSimple size={20} className="text-[#001588]" />
                  <span className="text-[10px] uppercase tracking-wider">Email</span>
                </a>
              )}
            </div>

            {/* Stage chip row — one-click transition */}
            <div className="mb-6">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">Move stage</div>
              <div className="flex flex-wrap gap-1.5">
                {STAGES.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => moveTo(selected.id, s.key)}
                    className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${
                      (selected.stage || "new") === s.key
                        ? "bg-[#001588] text-white border-[#001588]"
                        : "bg-white text-gray-700 border-neutral-300 hover:border-[#001588]"
                    }`}
                    data-testid={`drawer-move-${s.key}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3 text-sm mb-6 border-t border-neutral-200 pt-6">
              <Field label="Email" value={selected.email || "—"} />
              <Field label="Phone" value={selected.phone || "—"} />
              <Field label="Source" value={selected.source || "—"} />
              <Field label="Interest" value={selected.interest || "—"} />
              <Field label="Listing" value={selected.listing_title || "—"} />
              <Field label="Message" value={selected.message || "—"} />
            </div>

            <label className="block mb-4">
              <span className="text-xs uppercase tracking-wider text-gray-500 block mb-2">Next follow-up</span>
              <input
                type="datetime-local"
                value={selected.next_action_at ? selected.next_action_at.slice(0, 16) : ""}
                onChange={(e) => setSelected({ ...selected, next_action_at: e.target.value })}
                className="w-full border border-gray-300 px-3 py-2 text-sm"
                data-testid="lead-next-action"
              />
            </label>

            <label className="block">
              <span className="text-xs uppercase tracking-wider text-gray-500 block mb-2">Notes</span>
              <textarea
                value={selected.notes || ""} onChange={(e) => setSelected({ ...selected, notes: e.target.value })}
                rows="5"
                className="w-full border border-gray-300 px-3 py-2 text-sm"
                data-testid="lead-notes"
                placeholder="Call outcome, viewing notes, next step…"
              />
            </label>

            <div className="flex gap-2 mt-4">
              <button onClick={saveNotes} className="bg-[#001588] text-white px-6 py-2 text-sm hover:bg-black transition-colors" data-testid="lead-save-notes">Save updates</button>
              <Link to={`/admin/leads/${selected.id}`} className="border border-neutral-300 px-6 py-2 text-sm hover:border-[#001588] transition-colors" data-testid="lead-open-timeline">Full timeline →</Link>
              <button onClick={() => setSelected(null)} className="border border-gray-300 px-6 py-2 text-sm ml-auto">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-gray-800 break-words">{value}</div>
    </div>
  );
}
